import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output
} from '@angular/core';

import {HomeFavorite} from '../../../shared/types/home-types/user-favorites.interface';
import {TooltipComponent} from '../../../shared/elements/tooltip/tooltip.component';
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {TYPE} from '../../../shared/types/types';
import {DomSanitizer} from '@angular/platform-browser';
import {SettingsMenuItemType} from '../../../shared/types/generated/settingsMenuItemType';
import {ModuleConfigurationService} from '../../../shared/services/module-configuration.service';
import {DashboardFavoriteResource} from '../../../shared/types/generated/dashboardFavoriteResource';
import {GenericModelType} from '../../../shared/types/generated/genericModelType';
import {ProfileService} from '../../../shared/services/profile.service';

@Component({
    selector: 'app-home-favorites',
    imports: [TooltipComponent, IconBadgeComponent, ContentComponent, TranslatePipe],
    templateUrl: './user-favorites.component.html',
    styleUrls: ['./user-favorites.component.scss']
})
export class HomeFavoritesComponent {
  public sanitizer: DomSanitizer = inject(DomSanitizer);
  private modConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private profileService: ProfileService = inject(ProfileService);

  @Input({required: true}) public favorites: DashboardFavoriteResource[] = [];
  @Input({required: true}) public icons: TYPE.UserMenuItem[] = [];
  @Input() public page:number = 1;
   /** Max links per page – controlled by parent (depends on breakpoint) */
  @Input() public pageSize:number = 4;

  @Output() private pageChange: EventEmitter<number>= new EventEmitter<number>();
  @Output() private favoriteClick:EventEmitter<HomeFavorite> = new EventEmitter<HomeFavorite>();

  // mapping favorites to include icon/color/name
  private get favoritesWithIcons(): HomeFavorite[] {
    return this.favorites.map((fav: DashboardFavoriteResource) => {
      let correspondingMenuItem: TYPE.UserMenuItem | undefined;
      if(fav.model.type === GenericModelType.KNOWLEDGEBASE_CONTENT || fav.model.type === GenericModelType.KNOWLEDGEBASE_PAGE) {
        correspondingMenuItem = this.icons.find(i => i.type === SettingsMenuItemType.KNOWLEDGEBASE);
      } else if(fav.model.type === GenericModelType.NEWS){
        correspondingMenuItem = this.icons.find(i => i.type === SettingsMenuItemType.NEWS);
      } else if(fav.model.type === GenericModelType.COMMUNITIES_TOPIC){
        correspondingMenuItem = this.icons.find(i => i.type === SettingsMenuItemType.COMMUNITIES);
      } else {
        correspondingMenuItem = this.icons.find(i => i.type === SettingsMenuItemType.CHAT);
      }

      const mappedData: HomeFavorite = {
        id: String(fav.model.id),
        module: fav.model.type,
        title: fav.title ?? '',
        icon: correspondingMenuItem?.icon ?? '',
        route: this.modConfigService.getRouteForGenericModelResource(fav.model.type, String(fav.model.id), String(fav.model.parent_id)) ?? '',
        color: correspondingMenuItem?.color ?? '',
        name: correspondingMenuItem?.name ?? '',
      };

      return mappedData;
    });
  }

  //pagination helpers
  public get totalPages(): number {
    if (!this.favoritesWithIcons.length || !this.pageSize) return 0;
    return Math.ceil(this.favoritesWithIcons.length / this.pageSize);
  }

  public get pageCount(): number {
    return this.totalPages;
  }
  
  public get pages(): number[] {
    if (!this.totalPages) return [];
    return Array.from({length: this.totalPages}, (_, i) => i + 1);
  }

  public get pagedFavorites(): HomeFavorite[] {
    if (!this.favoritesWithIcons.length) return [];
    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.favoritesWithIcons.slice(start, end);
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.pageChange.emit(page);
  }

  public onFavoriteClick(fav: HomeFavorite): void {
    this.favoriteClick.emit(fav);
  }

  public trackById(_index: number, fav: HomeFavorite): string {
    return fav.id;
  }
}
